import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { ProtectedRoute } from './ProtectedRoute';
import { RoleRoute } from './RoleRoute';
import { ROLES } from '@/constants/roles';

// Root wrapper — provides Toaster for all routes
function Root() {
  return (
    <>
      <Outlet />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#0A1931',
            color: '#F6FAFD',
            borderRadius: '8px',
            fontSize: '14px',
            boxShadow: '0 8px 24px rgb(10 25 49 / 0.25)',
          },
          success: { iconTheme: { primary: '#16a34a', secondary: '#F6FAFD' } },
          error: { iconTheme: { primary: '#dc2626', secondary: '#F6FAFD' } },
        }}
      />
    </>
  );
}

// ── Lazy page imports ────────────────────────────────────────
// Auth
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
// const ForgotPasswordPage = lazy(() => import('@pages/auth/ForgotPasswordPage'));
// const ResetPasswordPage  = lazy(() => import('@pages/auth/ResetPasswordPage'));

// // App shell
const AppLayout = lazy(() => import('../components/layout/AppLayout'));

// // Dashboard
const DashboardPage = lazy(() => import('@pages/dashboard/DashboardPage'));

// // Projects
const ProjectsPage = lazy(() => import('@pages/projects/ProjectsListPage'));
const ProjectCreatePage = lazy(() => import('@pages/projects/ProjectCreatePage'));
const ProjectOverview = lazy(() => import('@pages/projects/ProjectOverviewPage'));

// Phase I
const EnquiryPage        = lazy(() => import('@pages/projects/phase1/EnquiryPage'));
const ProjectMasterPage  = lazy(() => import('@pages/projects/phase1/ProjectMasterPage'));
const WBSPage            = lazy(() => import('@pages/projects/phase1/WBSPage'));
const BudgetApprovalPage = lazy(() => import('@pages/projects/phase1/BudgetApprovalPage'));

// Phase II
const SiteVisitPage           = lazy(() => import('@pages/projects/phase2/SiteVisitPage'));
const DesignFinalisationPage  = lazy(() => import('@pages/projects/phase2/DesignFinalisationPage'));
const DrawingsSignoffPage     = lazy(() => import('@pages/projects/phase2/DrawingsSignoffPage'));
const WorksStartPage          = lazy(() => import('@pages/projects/phase2/WorksStartPage'));


// const ProjectWBS         = lazy(() => import('@pages/projects/tabs/ProjectWBS'));
// const ProjectDesign      = lazy(() => import('@pages/projects/tabs/ProjectDesign'));
// const ProjectMaterials   = lazy(() => import('@pages/projects/tabs/ProjectMaterials'));
// const ProjectVendors     = lazy(() => import('@pages/projects/tabs/ProjectVendors'));
// const ProjectSchedule    = lazy(() => import('@pages/projects/tabs/ProjectSchedule'));
// const ProjectFinancials  = lazy(() => import('@pages/projects/tabs/ProjectFinancials'));
// const ProjectSiteVisits  = lazy(() => import('@pages/projects/tabs/ProjectSiteVisits'));

// // Clients
const ClientsPage = lazy(() => import('@pages/clients/ClientsListPage'));
const ClientCreatePage = lazy(() => import('@pages/clients/ClientCreatePage'));
const ClientDetailPage = lazy(() => import('@pages/clients/ClientDetailPage'));

// // Vendors
// const VendorsPage        = lazy(() => import('@pages/vendors/VendorsPage'));
// const VendorDetailPage   = lazy(() => import('@pages/vendors/VendorDetailPage'));

// // Reports
// const ReportsPage        = lazy(() => import('@pages/reports/ReportsPage'));
// const PLReportPage       = lazy(() => import('@pages/reports/PLReportPage'));
// const BudgetVsActualPage = lazy(() => import('@pages/reports/BudgetVsActualPage'));
// const BusinessSummaryPage= lazy(() => import('@pages/reports/BusinessSummaryPage'));

// // Settings (Admin only)
// const SettingsPage       = lazy(() => import('@pages/settings/SettingsPage'));
// const UsersPage          = lazy(() => import('@pages/settings/UsersPage'));

// Fallback loader
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-frost">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-sky border-t-ocean" />
        <span className="text-sm text-steel">Loading…</span>
      </div>
    </div>
  );
}

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// ── Router ───────────────────────────────────────────────────
export const router = createBrowserRouter([
  {
    // Root wrapper — renders Toaster once for all routes
    element: <Root />,
    children: [
      // ── Public routes ────────────────────────────────────────
      {
        path: '/login',
        element: <S><LoginPage /></S>,
      },
      //     {
      //       path: '/forgot-password',
      //       element: <S><ForgotPasswordPage /></S>,
      //     },
      //     {
      //       path: '/reset-password',
      //       element: <S><ResetPasswordPage /></S>,
      //     },

      //     // ── Protected app routes ─────────────────────────────────
      {
        element: (
          <ProtectedRoute>
            <S><AppLayout /></S>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },

          // Dashboard
          { path: 'dashboard', element: <S><DashboardPage /></S> },

          // Projects
          { path: 'projects', element: <S><ProjectsPage /></S> },
          { path: 'projects/new', element: <S><ProjectCreatePage /></S> },
          {
            path: 'projects/:projectId',
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: <S><ProjectOverview /></S> },
              // Phase I — Foundation
              { path: 'enquiries',    element: <S><EnquiryPage /></S> },
              { path: 'master',       element: <S><ProjectMasterPage /></S> },
              { path: 'wbs',          element: <S><WBSPage /></S> },
              { path: 'budget',       element: <S><BudgetApprovalPage /></S> },
              // Phase II — Design & Execution
              { path: 'site-visits',       element: <S><SiteVisitPage /></S> },
              { path: 'design',            element: <S><DesignFinalisationPage /></S> },
              { path: 'drawings',          element: <S><DrawingsSignoffPage /></S> },
              { path: 'works-start',       element: <S><WorksStartPage /></S> },
              // { path: 'design',        element: <S><ProjectDesign /></S> },
              // { path: 'materials',     element: <S><ProjectMaterials /></S> },
              // { path: 'vendors',       element: <S><ProjectVendors /></S> },
              // { path: 'schedule',      element: <S><ProjectSchedule /></S> },
              // { path: 'financials',    element: <S><ProjectFinancials /></S> },
              // { path: 'site-visits',   element: <S><ProjectSiteVisits /></S> },
            ],
          },

          // Clients
          { path: 'clients', element: <S><ClientsPage /></S> },
          { path: 'clients/new', element: <S><ClientCreatePage /></S> },
          { path: 'clients/:clientId', element: <S><ClientDetailPage /></S> },


          // // Vendors
          // { path: 'vendors',              element: <S><VendorsPage /></S> },
          // { path: 'vendors/:vendorId',    element: <S><VendorDetailPage /></S> },

          // // Reports
          // { path: 'reports',              element: <S><ReportsPage /></S> },
          // { path: 'reports/pl/:projectId',element: <S><PLReportPage /></S> },
          // { path: 'reports/budget/:projectId', element: <S><BudgetVsActualPage /></S> },
          // { path: 'reports/summary',      element: <S><BusinessSummaryPage /></S> },

          // // Settings — Admin only
          // {
          //   path: 'settings',
          //   element: (
          //     <RoleRoute allowed={[ROLES.ADMIN]}>
          //       <S><SettingsPage /></S>
          //     </RoleRoute>
          //   ),
          // },
          // {
          //   path: 'settings/users',
          //   element: (
          //     <RoleRoute allowed={[ROLES.ADMIN]}>
          //       <S><UsersPage /></S>
          //     </RoleRoute>
          //   ),
          // },
        ],
      },

      // ── 404 ──────────────────────────────────────────────────
      {
        path: '*',
        element: (
          <div className="flex h-screen flex-col items-center justify-center gap-4 bg-frost">
            <span className="rounded-lg bg-ocean px-4 py-2 text-xs font-semibold uppercase tracking-widest text-frost">
              404
            </span>
            <h1 className="text-2xl font-bold text-navy">Page Not Found</h1>
            <a href="/dashboard" className="text-sm text-steel hover:text-ocean">
              ← Back to Dashboard
            </a>
          </div>
        ),
      },
    ],
  },
]);